import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updatePassword } from "../../store/slices/thunk";
import { toast, ToastContainer } from "react-toastify";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import "./changepassword.scss";

const Changepassword = () => {
  const [changePassword, setChangePassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: any) => {
    setChangePassword({
      ...changePassword,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { currentPassword, newPassword, confirmPassword } = changePassword;
      if (currentPassword === "" || newPassword === "") {
        toast.error("Please fill all fields!");
        setLoading(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error("New password and confirm password do not match!");
        setLoading(false);
        return;
      }
      const response: any = await dispatch(
        updatePassword({ currentPassword, newPassword })
      );
      if (response && response.isSuccess) {
        toast.success("Password changed successfully!");
        setChangePassword({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        toast.error(response.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("An error occurred while changing the password.");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#ffffff",
      height: "48px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#e2e8f0" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 1 },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
      fontSize: "14px",
      "&::placeholder": { color: "#94a3b8", opacity: 1 },
    },
  };

  return (
    <div
      style={{
        backgroundColor: "transparent",
        minHeight: "100vh",
        overflow: "auto",
        padding: "20px 15px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="container"
        style={{
          maxWidth: "1050px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ToastContainer />

        {/* Top Navigation Bar */}
        <div
          className="d-flex justify-content-between align-items-center mb-4"
          style={{ flexWrap: "wrap", gap: "12px" }}
        >
          <div className="d-flex align-items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#3b82f6",
                flexShrink: 0,
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </button>
            <div>
              <h2
                className="fw-bold m-0"
                style={{
                  color: "#0f172a",
                  fontSize: "24px",
                  fontFamily: "sans-serif",
                  lineHeight: 1.2,
                }}
              >
                Change Password
              </h2>
              <p
                className="m-0"
                style={{ color: "#64748b", fontSize: "14px" }}
              >
                Update your admin account password
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            style={{
              height: "40px",
              padding: "0 16px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              color: "#3b82f6",
              fontSize: "14px",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            <ArrowBackIcon fontSize="small" />
            Back To List
          </button>
        </div>

        {/* Main Card */}
        <div
          className="card border-0 shadow-sm p-4 p-md-5"
          style={{
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            flex: 1,
            overflow: "auto",
          }}
        >
          {/* Shield Icon */}
          <div
            className="d-flex justify-content-center mb-4 mt-2"
          >
            <div
              className="position-relative d-flex align-items-center justify-content-center"
              style={{ width: "90px", height: "100px" }}
            >
              {/* Decorative dots */}
              <div
                className="position-absolute rounded-circle"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#3b82f6",
                  opacity: 0.15,
                  top: "8px",
                  left: "-12px",
                }}
              ></div>
              <div
                className="position-absolute rounded-circle"
                style={{
                  width: "7px",
                  height: "7px",
                  backgroundColor: "#3b82f6",
                  opacity: 0.2,
                  bottom: "22px",
                  right: "-8px",
                }}
              ></div>

              {/* Shield */}
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "72px",
                  height: "82px",
                  backgroundColor: "#eff6ff",
                  clipPath:
                    "polygon(50% 0%, 100% 20%, 100% 75%, 50% 100%, 0% 75%, 0% 20%)",
                }}
              >
                <div
                  style={{
                    color: "#1d4ed8",
                    transform: "scale(1.3)",
                  }}
                >
                  <LockOutlined fontSize="medium" />
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="d-flex justify-content-center"
          >
            <div
              className="w-100"
              style={{ maxWidth: "500px" }}
            >
              {/* Current Password */}
              <div className="mb-4">
                <label
                  className="d-block mb-2 fw-semibold"
                  style={{
                    fontSize: "14px",
                    color: "#1e293b",
                  }}
                >
                  Current Password{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <TextField
                  type={showOldPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  name="currentPassword"
                  value={changePassword.currentPassword}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "#3b82f6", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowOldPassword(!showOldPassword)
                            }
                            edge="end"
                            size="small"
                          >
                            {showOldPassword ? (
                              <VisibilityOff
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            ) : (
                              <Visibility
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>

              {/* New Password */}
              <div className="mb-4">
                <label
                  className="d-block mb-2 fw-semibold"
                  style={{
                    fontSize: "14px",
                    color: "#1e293b",
                  }}
                >
                  New Password{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <TextField
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  name="newPassword"
                  value={changePassword.newPassword}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "#3b82f6", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowNewPassword(!showNewPassword)
                            }
                            edge="end"
                            size="small"
                          >
                            {showNewPassword ? (
                              <VisibilityOff
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            ) : (
                              <Visibility
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>

              {/* Confirm New Password */}
              <div className="mb-4">
                <label
                  className="d-block mb-2 fw-semibold"
                  style={{
                    fontSize: "14px",
                    color: "#1e293b",
                  }}
                >
                  Confirm New Password{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <TextField
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  name="confirmPassword"
                  value={changePassword.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "#3b82f6", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            ) : (
                              <Visibility
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>

              {/* Submit Button */}
              <div className="mb-2">
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LockOutlined sx={{ fontSize: 18 }} />
                    )
                  }
                  style={{
                    height: "50px",
                    textTransform: "none",
                    background: "#0066ff",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "15px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px -1px rgba(0, 102, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className="text-center mt-4"
          style={{
            fontSize: "13px",
            color: "#94a3b8",
            fontFamily: "sans-serif",
          }}
        >
          © 2024 Van Wala Operations System. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Changepassword;
